import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, Instagram, Facebook } from "lucide-react";
import { BRAND, WA_LINK } from "@/data/content";
import useInstagramFeed from "@/hooks/useInstagramFeed";
import Seo from "@/components/site/Seo";

export default function Contact() {
  const { loading, live, items } = useInstagramFeed(9);
  return (
    <div className="bg-[#FDFBF6]">
      <Seo
        title="Contact Indian Alchemy — Premium Catering in Gurugram, Delhi NCR"
        description="Reach the Indian Alchemy × Platinum Hospitality kitchen in Udyog Vihar, Gurugram. Call, WhatsApp or email us for luxury event catering & corporate cafeteria services across Delhi NCR."
        path="/contact"
        keywords="contact Indian Alchemy Gurugram, catering company Udyog Vihar phone, premium caterer Delhi NCR contact"
      />
      {/* HEADER */}
      <section className="pt-32 md:pt-40 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            Contact · Kitchen & Team
          </div>
          <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#3B1F1A] max-w-4xl">
            Say <em className="italic text-[#C9A961]">hello</em>.
          </h1>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.a
              href={`tel:${BRAND.phoneJhanvi.replace(/\s+/g, "")}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 border border-[#C9A961]/30 hover:border-[#C9A961] transition-colors group"
            >
              <Phone className="text-[#C9A961]" size={18} />
              <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
                Jhanvi Celly Saxena · CMO
              </div>
              <div className="mt-2 font-serif text-2xl text-[#3B1F1A] group-hover:text-[#C9A961]">
                {BRAND.phoneJhanvi}
              </div>
            </motion.a>

            <motion.a
              href={`tel:${BRAND.phoneAtul.replace(/\s+/g, "")}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 border border-[#D42B2B]/30 hover:border-[#D42B2B] transition-colors group"
            >
              <Phone className="text-[#D42B2B]" size={18} />
              <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#D42B2B]">
                Atul Celly · Proprietor
              </div>
              <div className="mt-2 font-serif text-2xl text-[#3B1F1A] group-hover:text-[#D42B2B]">
                {BRAND.phoneAtul}
              </div>
            </motion.a>

            <motion.a
              href={`mailto:${BRAND.email}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 border border-[#3B1F1A]/10 hover:border-[#C9A961] transition-colors group md:col-span-2"
            >
              <Mail className="text-[#C9A961]" size={18} />
              <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
                Email
              </div>
              <div className="mt-2 font-serif text-2xl text-[#3B1F1A] group-hover:text-[#C9A961]">
                {BRAND.email}
              </div>
              <div className="mt-1 text-sm text-[#3B1F1A]/60">{BRAND.phEmail}</div>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 border border-[#3B1F1A]/10 md:col-span-2 flex flex-col md:flex-row md:items-start md:justify-between gap-6"
            >
              <div>
                <MapPin className="text-[#C9A961]" size={18} />
                <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
                  Central Kitchen · Head Office
                </div>
                <div className="mt-2 font-serif text-xl text-[#3B1F1A] max-w-md">
                  {BRAND.address}
                </div>
                <div className="mt-2 text-sm text-[#3B1F1A]/60">{BRAND.hours}</div>
              </div>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 h-11 px-5 rounded-full bg-[#25D366] text-white text-xs uppercase tracking-[0.24em] hover:opacity-90"
              >
                <MessageCircle size={14} /> WhatsApp us
              </a>
            </motion.div>
          </div>

          {/* MAP + SOCIAL */}
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] w-full overflow-hidden border border-[#3B1F1A]/10">
              <iframe
                title="Indian Alchemy · Udyog Vihar"
                loading="lazy"
                src="https://www.google.com/maps?q=Udyog+Vihar+Phase+III,+Gurugram+122016&output=embed"
                className="w-full h-full grayscale-[0.3]"
              />
            </div>
            <div className="mt-6 flex items-center gap-4">
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#3B1F1A] border-b border-[#C9A961] pb-1 hover:text-[#C9A961]"
              >
                <Instagram size={14} /> {BRAND.instagramHandle}
              </a>
              <a
                href={BRAND.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#3B1F1A] border-b border-[#C9A961] pb-1 hover:text-[#C9A961]"
              >
                <Facebook size={14} /> Facebook
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INSTA STRIP */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            {live ? (
              <>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#C9A961] animate-pulse" />
                Live from {BRAND.instagramHandle}
              </>
            ) : (
              <>Latest from {BRAND.instagramHandle}</>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2">
            {loading &&
              Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="aspect-square bg-[#F5C9B0]/40 animate-pulse" />
              ))}
            {!loading &&
              items.map((it) => (
                <a
                  key={it.id}
                  href={it.permalink || BRAND.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square overflow-hidden group relative"
                  aria-label={it.caption?.slice(0, 80) || "Instagram post"}
                >
                  <img
                    src={it.image}
                    alt={it.caption?.slice(0, 80) || "Indian Alchemy post"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {it.media_type === "VIDEO" && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-[#3B1F1A]/70 text-[#FDFBF6] px-1.5 py-0.5">
                      Reel
                    </span>
                  )}
                </a>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
